"use client";

import { stegaClean } from "@sanity/client/stega";
import { useMemo } from "react";

import { useHubspotForm } from "@/hooks/useHubSpotForm";
import { getLogger } from "@/lib/logger";
import { ModuleHubSpotForm } from "@/lib/types";

interface HubSpotFormModuleProps {
  module: ModuleHubSpotForm;
}

const log = getLogger("HubSpotFormModule");

export function HubSpotFormModule({ module }: HubSpotFormModuleProps) {
  const rawFormId = module.hubSpotForm ?? "";
  const formId = stegaClean(rawFormId);
  const portalId = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID ?? "";
  const targetId = `hubspot-form-${formId}`;

  const formProps = useMemo(
    () => ({
      portalId: portalId,
      formId: formId,
      target: `#${targetId}`,
    }),
    [portalId, formId, targetId],
  );

  const { isFormCreated, isError, error } = useHubspotForm(formProps);

  if (!formId || !portalId || isError) {
    log.error("Failed to load HubSpot form", {
      error: error instanceof Error ? error.message : error || "Failed to load HubSpot form",
    });
    return null;
  }

  return (
    <section className="module-hubspot-form">
      <div className="container mx-auto">
        {!isFormCreated && !isError && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="relative h-12 w-12">
              <div className="absolute inset-0 rounded-full border-4 border-slate-200"></div>
              <div className="absolute inset-0 animate-spin rounded-full border-4 border-transparent border-t-slate-600"></div>
            </div>
            <p className="mt-4 text-sm text-slate-500">Loading...</p>
          </div>
        )}
        {isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-800">
              {error instanceof Error ? error.message : error || "Failed to load HubSpot form"}
            </p>
          </div>
        )}
        <div id={targetId} />
      </div>
    </section>
  );
}
